import { Component, Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { BrandCampaign } from "../../../../shared/models/brandCampaign";
import { BrandCampaignCategory } from "../../../../shared/models/brandCampaignCategory";
import { BrandCampaignDetails } from "../../../../shared/models/brandCampaignDetail";
import { cloneWith } from "../../../../shared/services/clonable";
import { DialogService } from "../../../../shared/services/dialog.service";
import { NotificationService } from "../../../../shared/services/notification.service";
import { BrandCampaignCategoryService } from "../../../shared/services/brand-campaign-category.service";

export interface CampaignCategoryFormDialogData {
  readonly campaign: BrandCampaign;
  readonly campaignDetails: BrandCampaignDetails;
  readonly category?: BrandCampaignCategory;
}

export type CampaignCategoryFormDialogResult =
  | "created"
  | "updated"
  | "deleted";

@Component({
  selector: "app-campaign-category-form-dialog",
  templateUrl: "./campaign-category-form-dialog.component.html",
  styleUrls: ["./campaign-category-form-dialog.component.scss"],
  providers: [BrandCampaignCategoryService],
})
export class CampaignCategoryFormDialogComponent {
  public saving = false;
  public removing = false;
  public readonly isEditing: boolean;
  public readonly category: BrandCampaignCategory;
  public readonly campaignDetails: BrandCampaignDetails;
  public readonly campaign: BrandCampaign;

  constructor(
    private readonly brandCampaignCategoryService: BrandCampaignCategoryService,
    private readonly notificationService: NotificationService,
    @Inject(MAT_DIALOG_DATA) data: CampaignCategoryFormDialogData,
    private readonly dialogService: DialogService,
    private readonly dialogRef: MatDialogRef<
      CampaignCategoryFormDialogComponent,
      CampaignCategoryFormDialogResult
    >,
  ) {
    this.campaign = data.campaign;
    this.campaignDetails = data.campaignDetails;
    if (data.category) {
      this.isEditing = true;
      this.category = cloneWith(data.category);
    } else {
      this.isEditing = false;
      this.category = new BrandCampaignCategory();
    }
  }

  public onActionCreateCategory(): void {
    this.saving = true;
    this.brandCampaignCategoryService
      .createCategory(this.campaign.id, this.campaignDetails.id, this.category)
      .subscribe({
        next: (category) => {
          this.saving = false;
          this.campaignDetails.categories.push(category);
          this.dialogRef.close("created");
        },
        error: () => {
          this.saving = false;
          this.notificationService.error("shared.errorSavingChanges");
        },
      });
  }

  public onActionUpdateCategory(): void {
    this.saving = true;
    this.brandCampaignCategoryService
      .updateCategory(this.campaign.id, this.campaignDetails.id, this.category)
      .subscribe({
        next: () => {
          this.saving = false;
          this.campaignDetails.categories = this.campaignDetails.categories.map(
            (c) => (c.id === this.category.id ? this.category : c),
          );
          this.dialogRef.close("updated");
        },
        error: () => {
          this.saving = false;
          this.notificationService.error("shared.errorSavingChanges");
        },
      });
  }

  public onActionDeleteCategory(): void {
    this.dialogService.confirm({
      title: "brand.campaign.categoryDialog.deleteConfirmTitle",
      message: "brand.campaign.categoryDialog.deleteConfirm",
      onConfirm: () => this.deleteCategory(),
    });
  }

  private deleteCategory(): void {
    this.removing = true;
    this.brandCampaignCategoryService
      .deleteCategory(
        this.campaign.id,
        this.campaignDetails.id,
        this.category.id,
      )
      .subscribe({
        next: () => {
          this.removing = false;
          this.campaignDetails.categories =
            this.campaignDetails.categories.filter(
              (c) => c.id !== this.category.id,
            );
          this.dialogRef.close("deleted");
        },
        error: () => {
          this.removing = false;
          this.notificationService.error("shared.errorSavingChanges");
        },
      });
  }
}
